'use client';

import { useCallback, useState } from 'react';
import { BonusShopProductType } from '@/api/cms/bonusShop';
import {
  type ExchangeCompointsPayload,
  exchangeCompointsFreespins,
  exchangeCompointsLootboxes,
  exchangeCompointsMoney,
} from '@/api/compPoints';
import {
  clearWagerFreeSuppression,
  suppressWagerFreeNotification,
} from '@/modules/promotions/bonusNotification';
import { useGetCommonStore } from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { formatExchangeError } from '../helpers';
import type { BonusShopProduct, BonusShopProductVersion } from '../types';

export enum ExchangeStatus {
  IDLE = 'idle',
  SUCCESS = 'success',
  ERROR = 'error',
}

const EXCHANGE_BY_TYPE: Record<
  BonusShopProductType,
  (data: ExchangeCompointsPayload) => ReturnType<typeof exchangeCompointsMoney>
> = {
  [BonusShopProductType.FREE_SPINS]: exchangeCompointsFreespins,
  [BonusShopProductType.CASH]: exchangeCompointsMoney,
  [BonusShopProductType.CASH_BONUS]: exchangeCompointsMoney,
  [BonusShopProductType.PREMIUM]: exchangeCompointsMoney,
  [BonusShopProductType.LOOTBOX]: exchangeCompointsLootboxes,
};

const WAGER_FREE_TYPES = [
  BonusShopProductType.CASH,
  BonusShopProductType.PREMIUM,
];

export const useBonusShopExchange = () => {
  const useCommonStore = useGetCommonStore();

  const currentWallet = useCommonStore.use.currentWallet();
  const user = useCommonStore.use.user();
  const fetchCompPoints = useCommonStore.use.fetchCompPoints();
  const fetchUser = useCommonStore.use.fetchUser();

  const currency = currentWallet?.currency || user?.currency || '';

  const [status, setStatus] = useState(ExchangeStatus.IDLE);
  const [isPending, setIsPending] = useState(false);

  const finishExchange = useCallback(() => {
    setStatus(ExchangeStatus.IDLE);
    fetchUser(REASON_FOR_UPDATING_USER.REFRESH_STATUSES);
  }, [fetchUser]);

  const resetStatus = useCallback(() => setStatus(ExchangeStatus.IDLE), []);

  const exchange = useCallback(
    async (product: BonusShopProduct, version?: BonusShopProductVersion) => {
      const group = version ? version.group : product.group;
      const points = version ? version.cpPrice : product.cpPrice;

      if (WAGER_FREE_TYPES.includes(product.type))
        suppressWagerFreeNotification();

      setIsPending(true);

      const { error: exchangeError } = await EXCHANGE_BY_TYPE[product.type]({
        group,
        points,
        currency,
      });

      setIsPending(false);

      if (exchangeError) {
        clearWagerFreeSuppression();
        setStatus(ExchangeStatus.ERROR);
        return { error: formatExchangeError(exchangeError), isSuccess: false };
      }

      await fetchCompPoints();

      setStatus(ExchangeStatus.SUCCESS);

      return { error: '', isSuccess: true };
    },
    [currency, fetchCompPoints]
  );

  return {
    currency,
    exchange,
    isPending,
    status,
    resetStatus,
    finishExchange,
  };
};
